import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projects } from '../mock';
import { ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectModal from '../components/ProjectModal';
import SeoHead from '../components/SeoHead';

const ProjectsPage = () => {
  // Category state is mirrored to the URL via ?category=<slug> so each
  // tab selection is shareable, bookmarkable and survives reload. The
  // dedicated /projects/<slug> routes (CategoryProjectsPage) still exist
  // for direct deep-links, but the design here is preserved unchanged.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState(initialCategory);
  const [selectedProject, setSelectedProject] = useState(null);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Keep React state in sync if the user uses Back/Forward or pastes a
  // new URL — the search param is the source of truth.
  useEffect(() => {
    const fromUrl = searchParams.get('category');
    setSelectedCategory(fromUrl);
  }, [searchParams]);

  const handleSelectCategory = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === null) {
      setSearchParams({}, { replace: false });
    } else {
      setSearchParams({ category: categoryId }, { replace: false });
    }
  };

  // Define category order and descriptions
  const categories = [
    {
      id: "film-festivals",
      title: "Film Festivals & Industry Programs",
      description: "Festival programming, industry events, and international coordination for major film festivals"
    },
    {
      id: "film-tv",
      title: "Films & TV Series",
      description: "Development, script consulting, production coordination, and creative guidance for international projects"
    },
    {
      id: "theatrical-shows",
      title: "Theatrical Shows",
      description: "Creative conception, direction, and production of immersive theatrical experiences and live performances"
    },
    {
      id: "exhibitions",
      title: "Exhibitions & Immersive Experiences",
      description: "Curation and production of immersive exhibitions, edutainment experiences, and large-scale interactive installations"
    },
    {
      id: "sports-ceremonies",
      title: "Sports Ceremonies",
      description: "Large-scale sports event production, opening & closing ceremonies for international tournaments, and live broadcast\u00A0coordination"
    },
    {
      id: "national-celebrations",
      title: "National Celebrations",
      description: "National day ceremonies, cultural celebrations, and large-scale national events production"
    }
  ];

  const categoryMapping = {
    "film-tv": "Films & TV Series",
    "film-festivals": "Industry & Cultural Programming",
    "theatrical-shows": "Theatrical Shows & Immersive Experiences",
    "exhibitions": "Exhibitions & Immersive Experiences",
    "sports-ceremonies": "Sports Ceremonies",
    "national-celebrations": "National Celebrations"
  };

  // Per-project badge pairs (status + role) for Films & TV vignettes
  const projectBadges = {
    2: { status: 'Completed', role: 'Executive Producer' },        // Another Day Shall Come
    1: { status: 'Completed', role: 'Associate Producer' },         // Dead Dog
    7: { status: 'Completed', role: 'Production Manager' },         // Le Jour du Seigneur
    3: { status: 'In Development', role: 'Writer / Showrunner' },   // Palmyra
    5: { status: 'Financing', role: 'Executive Producer' },         // Two Birds, One Stone
    4: { status: 'In Development', role: 'Writer / Director' },     // The Yellow Line
    6: { status: 'In Development', role: 'Development Executive' }, // Cinemoz Dev Slate
  };

  // Get projects for each category
  const getProjectsByCategory = (categoryId) => {
    const categoryName = categoryMapping[categoryId];
    return projects.filter(p => p.category === categoryName);
  };

  // Get all projects grouped
  const allProjectsByCategory = categories.map(cat => ({
    ...cat,
    projects: getProjectsByCategory(cat.id)
  }));

  return (
    <div className="min-h-screen bg-cream">
      <SeoHead
        title="Portfolio – Films, TV, Festivals & Live Productions | Carol Mezher"
        description="Selected works across films and TV series, international film festivals, theatrical shows, immersive exhibitions, sports ceremonies and national celebrations."
        path="/projects"
        ogType="website"
      />
      <Header />

      <section className="pt-32 pb-12 bg-midnight-blue relative overflow-hidden">
        {/* Subtle pattern backdrop */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.10]"
          style={{
            backgroundImage: 'url(/hero-pattern.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '760px auto',
            backgroundPosition: 'top left',
          }}
        />

        <div className="container mx-auto px-8 md:px-16 relative z-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-playfair font-bold text-5xl md:text-6xl text-white">
                Portfolio
              </h1>
            </div>

            {/* Tab Navigation */}
            <div className="flex flex-wrap justify-center items-center gap-3 mb-12 max-w-6xl mx-auto">
              <button
                onClick={() => handleSelectCategory(null)}
                className={`w-full sm:w-auto px-6 py-3 rounded-full font-montserrat font-semibold transition-all whitespace-nowrap text-center ${
                  selectedCategory === null
                    ? 'bg-terracotta text-white shadow-lg'
                    : 'bg-white/10 text-white hover:bg-terracotta/32 backdrop-blur-sm border border-white/20'
                }`}
              >
                All Projects
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleSelectCategory(cat.id)}
                  className={`w-full sm:w-auto px-6 py-3 rounded-full font-montserrat font-semibold transition-all whitespace-nowrap text-center ${
                    selectedCategory === cat.id
                      ? 'bg-terracotta text-white shadow-lg'
                      : 'bg-white/10 text-white hover:bg-terracotta/32 backdrop-blur-sm border border-white/20'
                  }`}
                >
                  {cat.title}
                </button>
              ))}
            </div>

            {/* Projects Display */}
            {selectedCategory === null ? (
              /* Show all projects with alternating subsection backgrounds */
              <div className="-mx-8 md:-mx-16 space-y-8 md:space-y-10">
                {allProjectsByCategory.map((category, idx) => {
                  const isLightBlue = idx % 2 === 0; // 0,2,4 light-blue cluster | 1,3,5 dark-blue cluster
                  return (
                    <div
                      key={category.id}
                      className={`relative overflow-hidden mx-4 md:mx-8 px-8 md:px-12 py-16 rounded-2xl shadow-xl ${
                        isLightBlue ? 'bg-terracotta' : 'bg-midnight-blue'
                      }`}
                    >
                      <div className="relative z-10 max-w-7xl mx-auto">
                        <div className="mb-8">
                          <h2 className="font-playfair font-bold text-3xl mb-2 text-white">
                            {category.title}
                          </h2>
                          <p className="max-w-4xl text-white/85">
                            {category.description}
                          </p>
                          <div className={`h-1 w-24 mt-3 ${isLightBlue ? 'bg-midnight-blue' : 'bg-terracotta'}`}></div>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {category.projects.map((project) => {
                            // Per-project focal point offsets:
                            // ids 2 (Another Day), 19 (Regeneration) → reveal upper portion
                            // id 1 (Dead Dog) → raised to also reveal English "DEAD DOG" title
                            // id 5 (Two Birds) → raised to reveal director name
                            // id 20 (Leila) → reveal lower portion (dancers)
                            let objectPosition = '50% 50%';
                            if ([2, 19].includes(project.id)) objectPosition = '50% 25%';
                            else if (project.id === 1) objectPosition = '50% 60%';
                            else if (project.id === 5) objectPosition = '50% 75%';
                            else if (project.id === 20) objectPosition = '50% 75%';

                            return (
                            <div
                              key={project.id}
                              onClick={() => setSelectedProject(project)}
                              className={`group cursor-pointer rounded-sm shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden ${
                                isLightBlue ? 'bg-midnight-blue' : 'bg-terracotta'
                              }`}
                            >
                              <div className="relative h-48 overflow-hidden bg-charcoal/5">
                                <img
                                  src={project.image}
                                  alt={project.title}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                  style={{ objectPosition }}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-midnight-blue/60 to-transparent opacity-70 group-hover:opacity-40 transition-opacity"></div>
                              </div>
                              <div className="p-5">
                                <h3
                                  className="font-playfair font-bold text-lg mb-2 transition-colors line-clamp-2 text-white group-hover:text-white/80"
                                  style={{ whiteSpace: 'pre-line' }}
                                >
                                  {project.title}
                                </h3>
                                <p className="text-sm line-clamp-2 text-whale-blue" style={{ whiteSpace: 'pre-line' }}>
                                  {project.subtitle}
                                </p>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Show selected category - each project as its own detached colored box */
              <div>
                {(() => {
                  const category = categories.find(c => c.id === selectedCategory);
                  const categoryIndex = categories.findIndex(c => c.id === selectedCategory);
                  const isLightBlue = categoryIndex % 2 === 0;
                  const categoryProjects = getProjectsByCategory(selectedCategory);
                  const bgClass = isLightBlue ? 'bg-terracotta' : 'bg-midnight-blue';
                  const accentClass = isLightBlue ? 'bg-midnight-blue' : 'bg-terracotta';
                  const accentTextClass = isLightBlue ? 'text-midnight-blue' : 'text-terracotta';

                  return (
                    <div className="-mx-8 md:-mx-16 space-y-8 md:space-y-10">
                      {/* Category header — its own detached box */}
                      <div className={`relative overflow-hidden mx-4 md:mx-8 px-8 md:px-12 py-12 rounded-2xl shadow-xl ${bgClass}`}>
                        <div className="max-w-7xl mx-auto">
                          <h2 className="font-playfair font-bold text-4xl text-white mb-2">
                            {category.title}
                          </h2>
                          <p className="text-white/85 max-w-4xl mb-4">{category.description}</p>
                          <div className={`h-1 w-24 ${accentClass}`}></div>
                        </div>
                      </div>

                      {/* Each project as its own detached box */}
                      {categoryProjects.map((project) => (
                        <article
                          key={project.id}
                          onClick={() => setSelectedProject(project)}
                          className={`group cursor-pointer relative overflow-hidden mx-4 md:mx-8 px-8 md:px-12 py-12 rounded-2xl shadow-xl transition-shadow duration-300 hover:shadow-2xl ${bgClass}`}
                        >
                          <div className="max-w-7xl mx-auto">
                            <div className="grid lg:grid-cols-12 gap-8 items-center">
                              {/* Image - always left, uniform 16:10 frame.
                                  object-contain preserves every image at
                                  its natural aspect ratio (nothing is
                                  cropped). The 16:10 frame is shorter
                                  than the previous 4:3, so the residual
                                  letterbox space is smaller and the
                                  cards read as roughly the same visual
                                  size despite different image shapes.
                                  The tinted background acts as
                                  intentional matting around portrait
                                  or square images. */}
                              <div className="lg:col-span-7 relative overflow-hidden rounded-sm shadow-xl">
                                <div className={`relative w-full aspect-[16/10] overflow-hidden ${isLightBlue ? 'bg-midnight-blue/20' : 'bg-terracotta/22'}`}>
                                  <img
                                    src={project.image}
                                    alt={project.title}
                                    loading="lazy"
                                    decoding="async"
                                    className="absolute inset-0 w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                                  />
                                </div>
                              </div>

                              {/* Info - always right */}
                              <div className="lg:col-span-5">
                                {/* Status + Role badges above the title (Films & TV Series only) */}
                                {projectBadges[project.id] && project.category === 'Films & TV Series' && (
                                  <div className="flex flex-wrap gap-2 mb-4">
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-montserrat font-semibold uppercase tracking-wider bg-whale-blue text-white shadow-sm">
                                      {projectBadges[project.id].status}
                                    </span>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-montserrat font-semibold uppercase tracking-wider text-white border border-terracotta">
                                      {projectBadges[project.id].role}
                                    </span>
                                  </div>
                                )}
                                <h2
                                  className={`font-playfair font-bold text-3xl md:text-4xl text-white mb-3 transition-colors ${
                                    // Hover colour mirrors accentTextClass: use the OPPOSITE
                                    // accent of the panel background so the title doesn't
                                    // vanish into a panel of the same colour. Was a flat
                                    // group-hover:text-terracotta which made the title
                                    // invisible on terracotta (steel-blue) panels —
                                    // Films & TV, Theatrical Shows, Sports Ceremonies.
                                    isLightBlue
                                      ? 'group-hover:text-midnight-blue'
                                      : 'group-hover:text-terracotta'
                                  }`}
                                  style={{ whiteSpace: 'pre-line' }}
                                >
                                  {project.title}
                                </h2>
                                {project.director && (
                                  <p className={`text-sm mb-4 italic font-semibold ${accentTextClass}`}>
                                    {project.director}
                                  </p>
                                )}
                                {project.subtitle && (
                                  <p className="text-lg text-whale-blue mb-4" style={{ whiteSpace: 'pre-line' }}>
                                    {project.subtitle}
                                  </p>
                                )}
                                {project.description && (
                                  <p className="text-base text-white/85 leading-relaxed mb-6 line-clamp-4">
                                    {project.description}
                                  </p>
                                )}
                                <button className={`inline-flex items-center gap-2 font-montserrat font-bold hover:gap-3 transition-all ${accentTextClass} hover:text-white`}>
                                  <span>View Full Details</span>
                                  <ArrowRight size={18} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />

      {/* Project Detail Modal — opens in-place so closing returns to the same subcategory list */}
      <ProjectModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </div>
  );
};

export default ProjectsPage;
